import { useState } from "react";
import { Trophy, Edit2, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCandidateAwards,
  useDeleteAward,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { AwardFormModal } from "@/pages/profile/employee/sections/components/AwardFormModal";
import type { AwardDetail } from "@/interface/award";
import type { ProfileDetail } from "@/interface";

export function AwardsSection({
  profileId,
}: {
  profileId?: number | string;
  profile?: ProfileDetail;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAward, setSelectedAward] = useState<AwardDetail | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const {
    data: candidateAwardsRes,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateAwards(profileId, { page: 1, page_size: 12 });
  const deleteMutation = useDeleteAward();

  const awards: AwardDetail[] =
    candidateAwardsRes?.pages?.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleOpenAdd = () => {
    setSelectedAward(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (award: AwardDetail) => {
    setSelectedAward(award);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-primary">Awards</h2>
          <p className="text-xs text-slate-500">Manage candidate awards</p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Award
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4 max-h-[500px]"
        >
          <div className="space-y-4">
            {awards.map((award: AwardDetail) => {
              const titleVal = award.award_title || "";
              const issuerVal = award.issuer || "";
              const dateVal = award.award_date || "";

              return (
                <div
                  key={award.id}
                  className="flex justify-between items-center p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 bg-amber-500/10 rounded-lg flex items-center justify-center shrink-0">
                      <Trophy className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                        {titleVal}
                      </h4>
                      <p className="text-xs text-slate-500">
                        {issuerVal}
                        {issuerVal && dateVal ? " - " : ""}
                        {dateVal}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenEdit(award)}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                    >
                      <Edit2 className="h-4 w-4 text-slate-400" />
                    </button>
                    <button
                      onClick={() => setDeleteTargetId(String(award.id))}
                      className="p-2 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-rose-400" />
                    </button>
                  </div>
                </div>
              );
            })}

            {awards.length === 0 && (
              <div className="text-center p-8 text-slate-500 text-sm">
                No awards added yet.
              </div>
            )}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more awards...
              </span>
            </div>
          )}
        </div>
      )}

      <AwardFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedAward}
        profileId={profileId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Award"
        description="Are you sure you want to remove this award?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
